import { useMemo, useState } from "react";
import type { Chatbot } from "@chat-yai/shared/admin-types";
import { copyToClipboard, buildEmbedSnippet } from "@/lib/copy";
import { useToast } from "@/contexts/ToastContext";
import { Icon } from "@/components/Icon";
import { Modal } from "@/components/Modal";

interface Props {
  chatbot: Chatbot;
  embedSnippet: string;
  siteDomain: string;
  tenantSlug: string;
}

type EmbedKind = "html" | "wordpress" | "shopify" | "wix" | "gtm";

const EMBED_TABS: Array<{ id: EmbedKind; label: string }> = [
  { id: "html", label: "HTML" },
  { id: "wordpress", label: "WordPress" },
  { id: "shopify", label: "Shopify" },
  { id: "wix", label: "Wix" },
  { id: "gtm", label: "Google Tag Manager" },
];

// Derive per-platform install instructions from the canonical script tag.
// Keeps the source of truth (snippet host + data-attributes) in one place
// and auto-updates the secondary tabs when the backend swaps the snippet.
function snippetForPlatform(base: string, kind: EmbedKind): string {
  switch (kind) {
    case "html":
      return base;
    case "wordpress":
      return `<!-- WordPress: paste into Appearance → Theme File Editor → header.php
just above </head>, OR install a "Header Footer Code" plugin and paste in
the "Scripts in <head>" box. -->
${base}`;
    case "shopify":
      return `<!-- Shopify: Online Store → Themes → Edit code → layout/theme.liquid.
Paste just above </head>. The script is async so it won't block checkout. -->
${base}`;
    case "wix":
      return `<!-- Wix: Settings → Custom Code → Add Custom Code.
- Code: paste below
- Add Code to Pages: All pages
- Place Code in: Head
-->
${base}`;
    case "gtm":
      return `<!-- Google Tag Manager: Tags → New → Custom HTML.
- Paste below
- Trigger: All Pages
-->
${base}`;
  }
}

export function ChatbotOverviewTab({
  chatbot,
  embedSnippet,
  siteDomain,
  tenantSlug,
}: Props): JSX.Element {
  const toast = useToast();
  const [copied, setCopied] = useState<EmbedKind | null>(null);
  const [activeTab, setActiveTab] = useState<EmbedKind>("html");
  const [testOpen, setTestOpen] = useState(false);

  const baseSnippet = useMemo(
    () =>
      embedSnippet ||
      buildEmbedSnippet({
        tenant: tenantSlug,
        site: siteDomain,
        chatbot: chatbot.slug,
      }),
    [embedSnippet, tenantSlug, siteDomain, chatbot.slug],
  );

  const snippet = snippetForPlatform(baseSnippet, activeTab);

  const onCopy = async (): Promise<void> => {
    const ok = await copyToClipboard(snippet);
    if (ok) {
      setCopied(activeTab);
      toast.push("success", "Embed snippet copied to clipboard.");
      setTimeout(() => setCopied(null), 2000);
    } else {
      toast.push("error", "Could not copy. Select the snippet manually.");
    }
  };

  return (
    <div className="col-2" style={{ marginTop: 16 }}>
      <div className="card" style={{ padding: 16 }}>
        <div className="sect-head">
          <h3>Basic info</h3>
        </div>
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "120px 1fr",
            gap: "8px 16px",
            margin: 0,
          }}
        >
          <dt style={{ color: "var(--text-3)", fontSize: 12 }}>Name</dt>
          <dd style={{ margin: 0, fontSize: 13 }}>{chatbot.name}</dd>
          <dt style={{ color: "var(--text-3)", fontSize: 12 }}>Slug</dt>
          <dd style={{ margin: 0 }}>
            <span className="mono" style={{ fontSize: 12 }}>
              {chatbot.slug}
            </span>
          </dd>
          <dt style={{ color: "var(--text-3)", fontSize: 12 }}>Status</dt>
          <dd style={{ margin: 0 }}>
            <span
              className={
                "chip " + (chatbot.status === "active" ? "chip-success" : "chip-warning")
              }
            >
              {chatbot.status}
            </span>
          </dd>
          <dt style={{ color: "var(--text-3)", fontSize: 12 }}>Default for site</dt>
          <dd style={{ margin: 0, fontSize: 13 }}>{chatbot.is_default ? "Yes" : "No"}</dd>
          <dt style={{ color: "var(--text-3)", fontSize: 12 }}>Created</dt>
          <dd style={{ margin: 0, fontSize: 12, color: "var(--text-3)" }} className="mono">
            {new Date(chatbot.created_at).toLocaleString()}
          </dd>
        </dl>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div className="sect-head">
          <h3>Install the widget</h3>
          <button
            className="btn btn-outline btn-sm"
            type="button"
            onClick={() => setTestOpen(true)}
            data-testid="test-embed-btn"
          >
            <Icon name="external" size={12} /> Test embed
          </button>
        </div>
        <p className="help" style={{ marginTop: 0 }}>
          Pick the platform you use. The snippet is the same async{" "}
          <code>&lt;script&gt;</code> tag — only the install location differs.
          The widget loads asynchronously and stays inside its own Shadow DOM
          on <span className="mono">{siteDomain || "your site"}</span>.
        </p>

        <div className="embed-tabs" role="tablist" data-testid="embed-tabs">
          {EMBED_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              className={"embed-tab " + (activeTab === t.id ? "active" : "")}
              onClick={() => {
                setActiveTab(t.id);
                setCopied(null);
              }}
              data-testid={`embed-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <pre
          className="mono"
          style={{
            background: "var(--surface-2)",
            padding: 12,
            borderRadius: 6,
            fontSize: 12,
            overflow: "auto",
            margin: 0,
            border: "1px solid var(--border)",
            color: "var(--text-1)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
          data-testid={`embed-snippet-${activeTab}`}
        >
          {snippet}
        </pre>
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={onCopy}
            data-testid={`copy-embed-${activeTab}`}
          >
            <Icon name={copied === activeTab ? "check" : "code"} size={12} />{" "}
            {copied === activeTab ? "Copied" : `Copy ${activeTab.toUpperCase()} snippet`}
          </button>
        </div>
      </div>

      <Modal
        title="Test embed"
        open={testOpen}
        onClose={() => setTestOpen(false)}
        footer={
          <button className="btn btn-primary btn-sm" onClick={() => setTestOpen(false)}>
            Close
          </button>
        }
      >
        <p className="help" style={{ marginTop: 0 }}>
          A quick sanity check that the script tag loads. The widget below is
          the same one your visitors will see — try sending a message.
        </p>
        <iframe
          title="Embed test"
          src={`/widget-preview.html?site=${encodeURIComponent(siteDomain || tenantSlug)}&chatbot=${encodeURIComponent(chatbot.slug)}`}
          className="widget-preview-iframe"
          style={{ height: 380 }}
          sandbox="allow-scripts"
          data-testid="test-embed-iframe"
        />
      </Modal>
    </div>
  );
}
